import clsx from "clsx";
import { AnimatePresence, motion } from "motion/react";
import {
  FC,
  PropsWithChildren,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from "react";
import { Account } from "rujira.js";
import { Drawer } from "vaul";
import deposit from "../../assets/images/deposit.gif";
import depositStatic from "../../assets/images/deposit.png";
import { useGlobalModalContext } from "../../context/GlobalModal";
import { useWindowSize } from "../../hooks/useWindowSize";
import { i18n } from "../../i18n";
import { AccountProvider, Provider } from "../../wallets";
import {
  AngleDown,
  Book,
  Building,
  Chart,
  ChartMixed,
  ChartUp,
  ChartUpDown,
  ChartUser,
  Code,
  Coins,
  CreditCard,
  Deposit,
  Earth,
  External,
  Gamepad,
  Gavel,
  Graduate,
  Home,
  Medium,
  MoneyTransfer,
  NFT,
  Nodes,
  PieChart,
  Seedling,
  Stake,
  Support,
  Swap,
  Terminal,
  Trophy,
  Users,
} from "../icons/Icons";
import { RujiraLogo } from "../logos/RujiraLogo";
import { Accounts, WalletProps, Wallets } from "./Accounts";
import { PendingDepositProps, PendingDeposits } from "./Pending";
import { ResolveLink } from "./ResolveLink";

export * from "./Accounts";
export * from "./QuickLauncher";

type HeaderProps = {
  accountProvider: AccountProvider;
  showAccount?: boolean;
  className?: string;
  domain?: string;
  routingElement: any;
  wallets: WalletProps[];
  ProviderIcon: FC<{ provider: Provider.Key; selected: boolean }>;
  onDeposit?: () => void;
  deposits?: Omit<PendingDepositProps, "accountProvider">;
  getAccountValue: (accoiunt: Account) => bigint;
  customActions?: ReactElement[];
};

export const Header = ({
  accountProvider,
  className,
  domain = "",
  routingElement,
  wallets,
  ProviderIcon,
  onDeposit,
  deposits,
  getAccountValue,
  customActions,
}: HeaderProps): ReactElement => {
  const [hover, setHover] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  const { accounts } = accountProvider;
  const { showModal } = useGlobalModalContext();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 56);
      setScrollY(window.scrollY <= 56 ? window.scrollY : 56);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const ConnectModal = () => {
    showModal({
      title: "Connect Your Account",
      backgroundClose: true,
      children: (
        <Wallets
          modalLayout={true}
          provider={accountProvider}
          wallets={wallets}
        />
      ),
    });
  };

  return (
    <Container
      style={{ backgroundColor: `rgba(15, 17, 23, ${(scrollY / 56) * 0.9})` }}
      className={clsx({
        [`${className}`]: className,
        "rujira-header--scrolled": isScrolled,
      })}>
      <ResolveLink as={routingElement} to="/">
        <Logo />
      </ResolveLink>
      <MainNav
        domain={domain}
        accountProvider={accountProvider}
        routingElement={routingElement}
      />
      <div className="ml-a mr-0 flex ai-c gradient-card-container">
        {deposits && <PendingDeposits {...deposits} />}
        {onDeposit && (
          <button
            id="deposit"
            className="transparent rujira-header__deposit condensed"
            onMouseOver={() => setHover(true)}
            onMouseOut={() => setHover(false)}
            onClick={() => {
              accounts?.length === 0 ? ConnectModal() : onDeposit();
            }}
          /* data-tooltip-id="global-tip"
          data-tooltip-content="Deposit" */
          >
            <img src={hover ? deposit : depositStatic} alt="Deposit" />
            <i18n.span>Deposit</i18n.span>
          </button>
        )}
        {/* <QuickLauncher domain={domain || ""} routingElement={routingElement} /> */}
        <Accounts
          provider={accountProvider}
          ProviderIcon={ProviderIcon}
          routingElement={routingElement}
          wallets={wallets}
          getValue={getAccountValue}
          //hideNetworkSwitch={hideNetworkSwitch}
          customActions={customActions}
        />
      </div>
      <MobileNav
        domain={domain}
        routingElement={routingElement}
        accountProvider={accountProvider}
      />
      {import.meta.env.MODE === "stage" && (
        <div className="rujira-header__warning condensed fw-500">
          Rujira Preview is an experimental environment. Products will sometimes
          be in early development and pre-audit. Please use at your own risk.
        </div>
      )}
    </Container>
  );
};

export const Container: React.FC<
  PropsWithChildren<{ className?: string; style?: React.CSSProperties }>
> = ({ children, className, style = {} }) => {
  return (
    <div
      className={clsx({
        "rujira-header": true,
        [`${className}`]: className,
      })}
      style={style}>
      {children}
    </div>
  );
};

export const Logo = () => {
  const size = useWindowSize();
  return (
    <RujiraLogo
      textColor="#fff"
      className="rujira-header__logo"
      showText={size.width >= 520}
      animate={true}
    />
  );
};

interface MainNavProps {
  domain: string;
  accountProvider?: AccountProvider;
  routingElement: any;
}

export const MainNav = ({
  domain,
  //accountProvider,
  routingElement,
}: MainNavProps): ReactElement => {
  //const { accounts } = accountProvider;
  const [showTrade, setShowTrade] = useState(false);
  const [showEarn, setShowEarn] = useState(false);
  const [showBorrow, setShowBorrow] = useState(false);
  const [showUtilities, setShowUtilities] = useState(false);
  const [showEntertainment, setShowEntertainment] = useState(false);
  const [showLearn, setShowLearn] = useState(false);

  const trade = useRef<HTMLDivElement | null>(null);
  const earn = useRef<HTMLDivElement | null>(null);
  const borrow = useRef<HTMLDivElement | null>(null);
  const utilities = useRef<HTMLDivElement | null>(null);
  const entertainment = useRef<HTMLDivElement | null>(null);
  const learn = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (
      showTrade ||
      showBorrow ||
      showUtilities ||
      showEarn ||
      showEntertainment ||
      showLearn
    ) {
      let popup = null;
      if (showTrade) {
        popup = trade.current;
      } else if (showEarn) {
        popup = earn.current;
      } else if (showBorrow) {
        popup = borrow.current;
      } else if (showUtilities) {
        popup = utilities.current;
      } else if (showEntertainment) {
        popup = entertainment.current;
      } else if (showLearn) {
        popup = learn.current;
      }
      if (popup) {
        const box = popup.getBoundingClientRect();
        const body = document.body;
        const docEl = document.documentElement;
        const scrollLeft =
          window.scrollX || docEl.scrollLeft || body.scrollLeft;
        const clientLeft = docEl.clientLeft || body.clientLeft || 0;
        const left = box.left + scrollLeft - clientLeft;
        if (left + box.width > window.innerWidth - 20) {
          const diff = Math.round(left + box.width - window.innerWidth + 20);
          popup.style.transform = "translateX(-" + diff + "px)";
        }
      }
    }
  }, [
    showTrade,
    showBorrow,
    showUtilities,
    showEarn,
    showEntertainment,
    showLearn,
  ]);

  return (
    <nav className="rujira-header__nav">
      {/* {accounts && accounts.length > 0 && (
        <ResolveLink as={routingElement} domain={domain} to="/portfolio">
          {i18n.t("Portfolio")}
        </ResolveLink>
      )} */}
      <span
        onMouseOver={() => setShowTrade(true)}
        onMouseOut={() => setShowTrade(false)}>
        {i18n.t("Trade")}
        <AngleDown />
        <AnimatePresence>
          {showTrade && (
            <SubTrade
              useRef={trade}
              domain={domain}
              routingElement={routingElement}
            />
          )}
        </AnimatePresence>
      </span>
      <span
        onMouseOver={() => setShowEarn(true)}
        onMouseOut={() => setShowEarn(false)}>
        {i18n.t("Earn")}
        <AngleDown />
        <AnimatePresence>
          {showEarn && (
            <SubEarn
              useRef={earn}
              domain={domain}
              routingElement={routingElement}
            />
          )}
        </AnimatePresence>
      </span>
      <span
        onMouseOver={() => setShowBorrow(true)}
        onMouseOut={() => setShowBorrow(false)}>
        {i18n.t("Borrow")}
        <AngleDown />
        <AnimatePresence>
          {showBorrow && (
            <SubBorrow
              useRef={borrow}
              domain={domain}
              routingElement={routingElement}
            />
          )}
        </AnimatePresence>
      </span>
      <span
        onMouseOver={() => setShowUtilities(true)}
        onMouseOut={() => setShowUtilities(false)}>
        {i18n.t("Utilities")}
        <AngleDown />
        <AnimatePresence>
          {showUtilities && (
            <SubUtilities
              useRef={utilities}
              domain={domain}
              routingElement={routingElement}
            />
          )}
        </AnimatePresence>
      </span>
      <span
        onMouseOver={() => setShowEntertainment(true)}
        onMouseOut={() => setShowEntertainment(false)}>
        {i18n.t("Entertainment")}
        <AngleDown />
        <AnimatePresence>
          {showEntertainment && (
            <SubEntertainment
              useRef={entertainment}
              domain={domain}
              routingElement={routingElement}
            />
          )}
        </AnimatePresence>
      </span>
      <span
        onMouseOver={() => setShowLearn(true)}
        onMouseOut={() => setShowLearn(false)}>
        {i18n.t("Learn & Help")}
        <AngleDown />
        <AnimatePresence>
          {showLearn && (
            <SubLearn
              useRef={learn}
              domain={domain}
              routingElement={routingElement}
            />
          )}
        </AnimatePresence>
      </span>
    </nav>
  );
};

export const MobileNav = ({
  domain,
  routingElement,
  accountProvider,
}: {
  domain: string;
  routingElement: any;
  accountProvider: AccountProvider;
}) => {
  const [showDrawer, setShowDrawer] = useState(false);
  const { accounts } = accountProvider;
  /*const mobile = useRef<HTMLDivElement | null>(null);

   useEffect(() => {
    if (showMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [showMobile]);*/

  let url = location.href;
  document.body.addEventListener(
    "click",
    () => {
      requestAnimationFrame(() => {
        if (url !== location.href) {
          setShowDrawer(false);
          url = location.href;
        }
      });
    },
    true
  );

  return (
    <Drawer.Root
      direction="left"
      open={showDrawer}
      onOpenChange={(open: boolean) => setShowDrawer(open)}>
      <button
        className="rujira-header__hamburger"
        onClick={() => setShowDrawer(true)}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
          <path
            d="M0 64H448v48H0V64zM0 224H448v48H0V224zM448 384v48H0V384H448z"
            fill="currentColor"
          />
        </svg>
      </button>
      <Drawer.Portal>
        <Drawer.Overlay className="drawer__overlay" />
        <Drawer.Content className="drawer__content drawer__content--left">
          <Drawer.Description className="visually-hidden">
            Main navigation menu
          </Drawer.Description>
          <Drawer.Title className="visually-hidden">Main Menu</Drawer.Title>

          <div className="drawer__card mobile-drawer">
            <ResolveLink
              as={routingElement}
              domain={domain}
              to="/"
              className="flex ai-c">
              <Home className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
              <div>
                <i18n.h4 className="fs-14 fw-600">Home</i18n.h4>
              </div>
            </ResolveLink>
            {accounts && accounts.length > 0 && (
              <ResolveLink
                as={routingElement}
                domain={domain}
                to="/portfolio"
                className="flex ai-c mt-2">
                <Coins className="color-grey h-3.5 w-a block mr-2 no-shrink" />
                <div>
                  <i18n.h4 className="fs-14 fw-600">Portfolio</i18n.h4>
                </div>
              </ResolveLink>
            )}
            <i18n.h4>Trade</i18n.h4>
            <SubTrade
              useRef={useRef<HTMLDivElement | null>(null)}
              domain={domain}
              routingElement={routingElement}
              className=""
            />
            <i18n.h4>Earn</i18n.h4>
            <SubEarn
              useRef={useRef<HTMLDivElement | null>(null)}
              domain={domain}
              routingElement={routingElement}
              className=""
            />
            <i18n.h4>Borrow</i18n.h4>
            <SubBorrow
              useRef={useRef<HTMLDivElement | null>(null)}
              domain={domain}
              routingElement={routingElement}
              className=""
            />
            <i18n.h4>Utilities</i18n.h4>
            <SubUtilities
              useRef={useRef<HTMLDivElement | null>(null)}
              domain={domain}
              routingElement={routingElement}
              className=""
            />
            <i18n.h4>Entertainment</i18n.h4>
            <SubEntertainment
              useRef={useRef<HTMLDivElement | null>(null)}
              domain={domain}
              routingElement={routingElement}
              className=""
            />
            <i18n.h4>Learn & Help</i18n.h4>
            <SubLearn
              useRef={useRef<HTMLDivElement | null>(null)}
              domain={domain}
              routingElement={routingElement}
              className=""
            />
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

const Popup = ({
  children,
  useRef,
  className = "rujira-header__popup sub-nav fw-400 p-2",
}: {
  children: React.ReactNode;
  useRef: React.RefObject<HTMLDivElement>;
  className?: string;
}) => (
  <motion.div
    ref={useRef}
    className={className}
    initial={{ opacity: 0, marginTop: -4 }}
    animate={{ opacity: 1, marginTop: 0 }}
    exit={{ opacity: 0, marginTop: -4 }}>
    {children}
  </motion.div>
);

type SubProps = {
  domain?: string;
  routingElement: any;
  useRef: React.RefObject<HTMLDivElement>;
  setShowMobile?: (e: any) => void;
  className?: string;
};

const SubTrade = ({ domain, routingElement, useRef, className }: SubProps) => (
  <Popup useRef={useRef} className={className}>
    <div className="grid w-60 mt-1">
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/swap"
        className="flex ai-s">
        <Swap className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600">Token Swap</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Swap tokens at the best available rate
          </i18n.p>
        </div>
      </ResolveLink>
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/trade"
        className="flex ai-s">
        <Chart className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600">Spot Trading</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Buy and sell on the spot market without slippage
          </i18n.p>
        </div>
      </ResolveLink>
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/perps/trade"
        className="flex ai-s">
        <ChartUpDown className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600">Perps</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Buy and sell perpetual futures contracts
          </i18n.p>
        </div>
      </ResolveLink>
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/index"
        className="flex ai-s">
        <PieChart className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600">Index</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Diversify your portfolio in one click
          </i18n.p>
        </div>
      </ResolveLink>
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/options"
        className="flex ai-s">
        <ChartUp className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600 flex ai-c">Options</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Designed to simplify options trading
          </i18n.p>
        </div>
      </ResolveLink>
    </div>
  </Popup>
);

const SubEarn = ({ domain, routingElement, useRef, className }: SubProps) => (
  <Popup useRef={useRef} className={className}>
    <div className="grid w-60 mt-1">
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/strategies/staking/RUJI"
        className="flex ai-s">
        <Stake className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600">Stake</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Earn a share of protocol revenue across all Rujira apps
          </i18n.p>
        </div>
      </ResolveLink>
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/strategies"
        className="flex ai-s">
        <Building className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600">Strategies</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Explore opportunities to earn yield on your tokens
          </i18n.p>
        </div>
      </ResolveLink>
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/liquidate"
        className="flex">
        <Gavel className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600">Liquidate</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Beat the bots and bid on liquidated collateral
          </i18n.p>
        </div>
      </ResolveLink>
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/launchpad"
        className="flex ai-s">
        <Seedling className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600 flex ai-c">Launchpad</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Early access to promising protocols &amp; opportunities
          </i18n.p>
        </div>
      </ResolveLink>
    </div>
  </Popup>
);

const SubBorrow = ({ domain, routingElement, useRef, className }: SubProps) => (
  <Popup useRef={useRef} className={className}>
    <div className="grid w-60 mt-1">
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/borrow"
        className="flex ai-s">
        <MoneyTransfer className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600 flex ai-c">Borrow</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Borrow assets using your tokens as collateral
          </i18n.p>
        </div>
      </ResolveLink>
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/mint"
        className="flex ai-s">
        <Deposit className="color-grey h-a w-3 block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600">Mint stablecoin</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Use your native BTC as collateral to mint...
          </i18n.p>
        </div>
      </ResolveLink>
    </div>
  </Popup>
);

const SubUtilities = ({
  domain,
  routingElement,
  useRef,
  className,
}: SubProps) => (
  <Popup useRef={useRef} className={className}>
    <div className="grid w-60 mt-1">
      <a
        href="https://ai.autorujira.app/"
        target="_blank"
        className="flex ai-s">
        <Terminal className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600">
            RUJI AI
            <External className="h-1.5 w-a ml-1" />
          </i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Use Rujira from a simple chat interface and deploy autonomous AI
            agents
          </i18n.p>
        </div>
      </a>
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/analytics"
        className="flex ai-s">
        <ChartMixed className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600">Analytics</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Discover RUJI tokenomics and analytics
          </i18n.p>
        </div>
      </ResolveLink>
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/buy"
        className="flex">
        <CreditCard className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600">Buy Crypto</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Instantly buy crypto at the best available rate
          </i18n.p>
        </div>
      </ResolveLink>
      <a href="https://daodao.zone" target="_blank" className="flex ai-s">
        <Users className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600 flex ai-c">
            DAODAO <External className="h-1.5 w-a ml-1" />
          </i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Created DAOs or Multisig and participate in governance
          </i18n.p>
        </div>
      </a>
    </div>
  </Popup>
);

const SubEntertainment = ({
  domain,
  routingElement,
  useRef,
  className,
}: SubProps) => (
  <Popup useRef={useRef} className={className}>
    <div className="grid w-60 mt-1">
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/leaderboard"
        className="flex ai-s">
        <Trophy className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600">Leagues</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Compete for a place in the leaderboard and earn RUJI and USDC
            rewards
          </i18n.p>
        </div>
      </ResolveLink>
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/games"
        className="flex ai-s">
        <Gamepad className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600">Games</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Test your reflexes, strategy and luck and compete in games
          </i18n.p>
        </div>
      </ResolveLink>
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/predict"
        className="flex">
        <ChartUser className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600">Predict</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Speculate on the outcome of real world events
          </i18n.p>
        </div>
      </ResolveLink>
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/omniverse"
        className="flex">
        <Nodes className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600">Omniverse</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Experience a DeFi world powered by lore, NFT's and quests
          </i18n.p>
        </div>
      </ResolveLink>
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/nfts"
        className="flex ai-s">
        <NFT className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600 flex ai-c">Collections</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            A sustainable NFT marketplace
          </i18n.p>
        </div>
      </ResolveLink>
    </div>
  </Popup>
);

const SubLearn = ({ domain, routingElement, useRef, className }: SubProps) => (
  <Popup useRef={useRef} className={className}>
    <div className="grid w-60 mt-1">
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/getstarted"
        className="flex ai-s">
        <Graduate className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600">Get Started</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            First steps into crypto and Rujira
          </i18n.p>
        </div>
      </ResolveLink>
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/ecosystem"
        className="flex ai-s">
        <Earth className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600">Ecosystem</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Protocols, products and services calling Rujira their home
          </i18n.p>
        </div>
      </ResolveLink>
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/education"
        className="flex ai-s">
        <Book className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600">Education</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Learn about crypto, finance and the Rujira ecosystem
          </i18n.p>
        </div>
      </ResolveLink>
      <a
        href="https://docs.rujira.network/"
        target="_blank"
        className="flex ai-s">
        <Code className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600 flex ai-c">
            Documentation <External className="h-1.5 w-a ml-1" />
          </i18n.h4>

          <i18n.p className="fs-12 mt-0.5">
            Dive deep into the Rujira network
          </i18n.p>
        </div>
      </a>
      <a
        href=" https://docs.rujira.network/developers/getting-started"
        target="_blank"
        className="flex ai-s">
        <Terminal className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600 flex ai-c">
            Developers
            <External className="h-1.5 w-a ml-1" />
          </i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Rujira is built for builders, get started here
          </i18n.p>
        </div>
      </a>
      {/* <ResolveLink
        as={routingElement}
        domain={domain}
        to="/developers"
        className="flex">
        <Terminal className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600">Developers</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Rujira is built for builders, get started here
          </i18n.p>
        </div>
      </ResolveLink> */}
      <ResolveLink
        as={routingElement}
        domain={domain}
        to="/support"
        className="flex">
        <Support className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600">Support</i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            View the FAQ and get help with your questions
          </i18n.p>
        </div>
      </ResolveLink>
      <a
        href="https://medium.com/rujiranetwork"
        target="_blank"
        className="flex ai-s">
        <Medium className="color-grey h-3.5 w-a block mr-2 no-shrink mt-0" />
        <div>
          <i18n.h4 className="fs-14 fw-600 flex ai-c">
            Articles <External className="h-1.5 w-a ml-1" />
          </i18n.h4>
          <i18n.p className="fs-12 mt-0.5">
            Your news source for everything Rujira related
          </i18n.p>
        </div>
      </a>
    </div>
  </Popup>
);
